import { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { questsService } from '../../Quests/services/quests-service';

interface GenerateOcdProfileModalProps {
    onGenerate: (ocdThemes: string[], questJourneyDay: number, extraGuidance: string) => Promise<void>;
    onClose: () => void;
}

const ocdThemeOptions = [
    { value: "contamination", label: "Contamination" },
    { value: "health-anxiety", label: "Health Anxiety" },
    { value: "harm", label: "Harm" },
    { value: "responsibility-negligence", label: "Responsibility / Negligence" },
    { value: "sexual", label: "Sexual" },
    { value: "pedophilia", label: "Pedophilia" },
    { value: "relationships", label: "Relationships" },
    { value: "religion-moral", label: "Religion / Moral" },
    { value: "identity", label: "Identity" },
    { value: "just-right-perfectionism", label: "Just Right / Perfectionism" },
    { value: "existential", label: "Existential" },
    { value: "sensorimotor-hyperawareness", label: "Sensorimotor / Hyperawareness" },
    { value: "pregnancy-postpartum", label: "Pregnancy / Postpartum" },
    { value: "magical-thinking", label: "Magical Thinking" },
    { value: "false-memory-past-events", label: "False Memory / Past Events" },
    { value: "checking-doubt", label: "Checking / Doubt" },
];

export const GenerateOcdProfileModal = ({ onGenerate, onClose }: GenerateOcdProfileModalProps) => {
    const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
    const [questJourneyDay, setQuestJourneyDay] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);
    const [questJourneyDays, setQuestJourneyDays] = useState<string[]>([]);
    const [extraGuidance, setExtraGuidance] = useState<string>('');

    useEffect(() => {
        const fetchQuestJourneyDays = async () => {
            const days = await questsService.getQuestJourneyDays();
            setQuestJourneyDays(days);
        };
        fetchQuestJourneyDays();
    }, []);

    const handleThemeToggle = (themeValue: string) => {
        setSelectedThemes(prev => {
            if (prev.includes(themeValue)) {
                return prev.filter(t => t !== themeValue);
            }
            return [...prev, themeValue];
        });
    };

    const handleGenerate = async () => {
        if (selectedThemes.length === 0) {
            alert('Please select at least one OCD theme');
            return;
        }

        setIsLoading(true);
        try {
            await onGenerate(selectedThemes, questJourneyDay, extraGuidance);
            onClose();
        } catch (error) {
            console.error('Error generating OCD profile:', error);
            alert('Failed to generate OCD profile. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">Generate OCD Profile</h2>
                    <p className="text-sm text-gray-600 mt-1">Select OCD themes and quest journey day to generate a new profile</p>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Quest Journey Day
                        </label>
                        <select
                            value={questJourneyDay}
                            onChange={(e) => setQuestJourneyDay(Number(e.target.value))}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                            disabled={isLoading}
                        >
                            {questJourneyDays.map((day) => {
                                const dayNumber = parseInt(day.replace('day_', ''));
                                return (
                                    <option key={day} value={dayNumber}>
                                        Day {dayNumber}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Extra Guidance (optional)
                        </label>
                        <textarea
                            value={extraGuidance}
                            onChange={(e) => setExtraGuidance(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                            disabled={isLoading}
                            placeholder="Enter any additional instructions or context for AI to generate the OCD profile..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            OCD Themes (Select multiple)
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {ocdThemeOptions.map((option) => (
                                <label
                                    key={option.value}
                                    className={`flex items-center p-3 border rounded-md cursor-pointer transition-colors ${selectedThemes.includes(option.value)
                                        ? 'bg-blue-50 border-blue-500'
                                        : 'bg-white border-gray-300 hover:border-gray-400'
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedThemes.includes(option.value)}
                                        onChange={() => handleThemeToggle(option.value)}
                                        disabled={isLoading}
                                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-gray-900">{option.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                    <Button
                        onClick={onClose}
                        variant="secondary"
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleGenerate}
                        variant="primary"
                        disabled={isLoading || selectedThemes.length === 0}
                    >
                        {isLoading ? 'Generating...' : 'Generate Profile'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

